'use strict';

const STORAGE_KEY = 'vineStockOrderOfferId';
const OFFER_BUTTON_SELECTOR = 'a[data-offer-id]';
const SWISS_COUNTRY_CODE = 'CH';
const LOCKED_CLASS = 'is-country-locked';
const NOTE_CLASS = 'vine-stock-order-form__country-note';
const HIDDEN_INPUT_SELECTOR = 'input[type="hidden"][data-country-lock-hidden]';

/**
 * @typedef {{ id: string, title?: string, noInternationalShipping?: boolean | string | number }} OfferConfig
 */

/**
 * @param {Element | null | undefined} root
 * @returns {OfferConfig[]}
 */
function parseOffersConfig(root) {
  const roots = [];

  if (root instanceof HTMLElement) {
    roots.push(root);
  }

  const documentRoot = document.querySelector(
    '[data-vine-stock-order-form], .vine-stock-order-form, .jf-site-content-vinestockorderform'
  );

  if (documentRoot instanceof HTMLElement && !roots.includes(documentRoot)) {
    roots.push(documentRoot);
  }

  for (const candidate of roots) {
    const raw = candidate.getAttribute('data-offers-config');
    if (!raw) {
      continue;
    }

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch {
      // Continue with fallback sources.
    }
  }

  return collectOffersFromButtons();
}

/**
 * @returns {OfferConfig[]}
 */
function collectOffersFromButtons() {
  /** @type {Map<string, OfferConfig>} */
  const offers = new Map();

  document.querySelectorAll(OFFER_BUTTON_SELECTOR).forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) {
      return;
    }

    const offerId = link.dataset.offerId;
    if (!offerId || offers.has(offerId)) {
      return;
    }

    offers.set(offerId, {
      id: offerId,
      noInternationalShipping: link.dataset.noInternationalShipping === 'true',
    });
  });

  return Array.from(offers.values());
}

/**
 * @param {HTMLSelectElement} field
 * @param {string} identifier
 * @returns {boolean}
 */
function matchesSelectField(field, identifier) {
  const suffix = `[${identifier}]`;

  if (field.name.endsWith(suffix)) {
    if (identifier === 'country' && field.name.endsWith('[recipientCountry]')) {
      return false;
    }

    return true;
  }

  if (identifier === 'country' && field.dataset.countryLockName?.endsWith(suffix)) {
    return true;
  }

  return false;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} identifier
 * @returns {HTMLSelectElement | null}
 */
function findSelectField(form, identifier) {
  const fields = form.querySelectorAll('select');

  for (const field of fields) {
    if (!(field instanceof HTMLSelectElement)) {
      continue;
    }

    if (matchesSelectField(field, identifier)) {
      return field;
    }
  }

  return null;
}

/**
 * @param {OfferConfig[]} offers
 * @param {string} offerId
 * @returns {OfferConfig | null}
 */
function getOfferById(offers, offerId) {
  return offers.find((offer) => offer.id === offerId) ?? null;
}

/**
 * @param {OfferConfig | null | undefined} offer
 * @returns {boolean}
 */
function requiresSwissShippingOnly(offer) {
  if (!offer) {
    return false;
  }

  return (
    offer.noInternationalShipping === true ||
    offer.noInternationalShipping === 'true' ||
    offer.noInternationalShipping === 1 ||
    offer.noInternationalShipping === '1'
  );
}

/**
 * @param {HTMLSelectElement} select
 * @returns {HTMLInputElement | null}
 */
function findHiddenCountryInput(select) {
  const container = select.closest('.clearfix') ?? select.parentElement;
  if (!container) {
    return null;
  }

  const hidden = container.querySelector(HIDDEN_INPUT_SELECTOR);
  return hidden instanceof HTMLInputElement ? hidden : null;
}

/**
 * @param {HTMLSelectElement} select
 * @returns {HTMLInputElement}
 */
function createHiddenCountryInput(select) {
  const hidden = document.createElement('input');
  hidden.type = 'hidden';
  hidden.setAttribute('data-country-lock-hidden', '');

  const container = select.closest('.clearfix') ?? select.parentElement;
  container?.appendChild(hidden);

  return hidden;
}

/**
 * @param {HTMLElement} select
 * @returns {HTMLElement | null}
 */
function findCountryFieldWrapper(select) {
  return select.closest('.clearfix');
}

/**
 * @param {HTMLElement} wrapper
 */
function removeCountryNote(wrapper) {
  wrapper.querySelector(`.${NOTE_CLASS}`)?.remove();
}

/**
 * @param {HTMLElement} wrapper
 */
function ensureCountryNote(wrapper) {
  removeCountryNote(wrapper);

  const note = document.createElement('p');
  note.className = NOTE_CLASS;
  note.textContent = 'Cette offre est réservée à la Suisse.';

  wrapper.appendChild(note);
}

/**
 * @param {HTMLSelectElement} select
 */
function lockCountryToSwitzerland(select) {
  const wrapper = findCountryFieldWrapper(select);
  if (!wrapper) {
    return;
  }

  select.value = SWISS_COUNTRY_CODE;
  select.disabled = true;
  select.setAttribute('aria-disabled', 'true');
  wrapper.classList.add(LOCKED_CLASS);

  let hidden = findHiddenCountryInput(select);
  if (!hidden) {
    hidden = createHiddenCountryInput(select);
  }

  if (select.name) {
    hidden.name = select.name;
    select.dataset.countryLockName = select.name;
    select.removeAttribute('name');
  } else if (select.dataset.countryLockName) {
    hidden.name = select.dataset.countryLockName;
  }

  hidden.value = SWISS_COUNTRY_CODE;
  ensureCountryNote(wrapper);
}

/**
 * @param {HTMLSelectElement} select
 */
function unlockCountry(select) {
  const wrapper = findCountryFieldWrapper(select);
  if (!wrapper) {
    return;
  }

  const hidden = findHiddenCountryInput(select);
  if (hidden?.name) {
    select.name = hidden.name;
  } else if (select.dataset.countryLockName) {
    select.name = select.dataset.countryLockName;
  }

  hidden?.remove();
  delete select.dataset.countryLockName;

  select.disabled = false;
  select.removeAttribute('aria-disabled');
  wrapper.classList.remove(LOCKED_CLASS);
  removeCountryNote(wrapper);
}

/**
 * @param {HTMLFormElement} form
 * @param {OfferConfig[]} offers
 * @param {string} offerId
 */
function applyOfferRules(form, offers, offerId) {
  const offerField = findSelectField(form, 'offer');
  const countryField = findSelectField(form, 'country');

  if (offerField && offerId) {
    const hasMatchingOption = Array.from(offerField.options).some(
      (option) => option.value === offerId
    );

    if (hasMatchingOption && offerField.value !== offerId) {
      offerField.value = offerId;
    }
  }

  if (countryField) {
    const selectedOfferId = offerId || offerField?.value || '';
    const offer = getOfferById(offers, selectedOfferId);

    if (requiresSwissShippingOnly(offer)) {
      lockCountryToSwitzerland(countryField);
    } else {
      unlockCountry(countryField);
    }
  }

  form.dispatchEvent(new CustomEvent('vinestock:pricing-source-changed', { bubbles: true }));
}

/**
 * @returns {string | null}
 */
function resolveInitialOfferId() {
  const urlOfferId = new URL(window.location.href).searchParams.get('offer');
  if (urlOfferId) {
    return urlOfferId;
  }

  const storedOfferId = sessionStorage.getItem(STORAGE_KEY);
  if (storedOfferId) {
    sessionStorage.removeItem(STORAGE_KEY);
    return storedOfferId;
  }

  return null;
}

/**
 * @returns {HTMLElement | null}
 */
function findFormRoot() {
  const root = document.querySelector(
    '[data-vine-stock-order-form], .vine-stock-order-form, .jf-site-content-vinestockorderform'
  );

  return root instanceof HTMLElement ? root : null;
}

let offerButtonListenerRegistered = false;

/**
 * @param {Element | null} root
 * @param {HTMLFormElement} form
 */
export function initOfferCountryRules(root, form) {
  const formRoot = root instanceof HTMLElement ? root : findFormRoot();
  const offers = parseOffersConfig(formRoot);
  const offerField = findSelectField(form, 'offer');

  if (!offerField) {
    return;
  }

  const initialOfferId = resolveInitialOfferId();
  if (initialOfferId) {
    applyOfferRules(form, offers, initialOfferId);
  } else {
    applyOfferRules(form, offers, offerField.value);
  }

  offerField.addEventListener('change', () => {
    applyOfferRules(form, offers, offerField.value);
  });

  if (offerButtonListenerRegistered) {
    return;
  }

  offerButtonListenerRegistered = true;

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const link = event.target.closest(OFFER_BUTTON_SELECTOR);
    if (!(link instanceof HTMLAnchorElement)) {
      return;
    }

    const offerId = link.dataset.offerId;
    if (!offerId) {
      return;
    }

    sessionStorage.setItem(STORAGE_KEY, offerId);

    const rootElement = findFormRoot();
    const orderForm = rootElement?.querySelector('form');
    if (!(orderForm instanceof HTMLFormElement)) {
      return;
    }

    const currentOffers = parseOffersConfig(rootElement);
    applyOfferRules(orderForm, currentOffers, offerId);
  });
}
